import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Gender } from "@bca-shared/models/_enums";
import { Form } from "@bca-shared/models/form";
import { IUser } from "@bca-shared/models/user";
import { UIService } from "@bca-core/services/ui.service";
import { SignatoryStatus } from "@bca-company-data/models/signatory-status-response";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "bca-user-form",
  templateUrl: "user-form.component.html",
  styleUrls: ["user-form.component.scss"],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class UserFormComponent extends Form<IUser> implements OnInit {
  @Input()
  userForm: FormGroup;
  @Input()
  hasEmailTooltip: boolean;
  @Input()
  middleNames: string[] = [];
  @Input()
  checkedSignatoryStatus: SignatoryStatus;

  @Output()
  resetStatus = new EventEmitter<void>();
  @Output()
  sendError = new EventEmitter<any>();

  private lastReportedLastNameError: string | undefined;
  private lastReportedInitialsError: string | undefined;
  private lastReportedPhoneNumberNameError: string | undefined;
  private lastReportedEmailError: string | undefined;

  genders: {
    value: Gender;
    displayText: string;
    id: number;
  }[] = [
    {
      value: Gender.MALE,
      displayText: "INPUT.MALE",
      id: Date.now(),
    },
    {
      value: Gender.FEMALE,
      displayText: "INPUT.FEMALE",
      id: Date.now(),
    },
    {
      value: Gender.NOT_RELEVANT,
      displayText: "INPUT.NOT_RELEVANT",
      id: Date.now(),
    },
  ];

  constructor(
    public _uiService: UIService,
    private _translate: TranslateService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.setFormGroup(this.userForm);
  }

  resetSignatoryStatus() {
    this.resetStatus.emit();
  }

  getGenderErrorKey(): string | undefined {
    if (
      this.isTouchedAndDirty("gender") &&
      this.hasError("gender", "required")
    ) {
      return "INPUT.GENDER.ERROR_REQUIRED";
    } else {
      return undefined;
    }
  }

  isSignatoryStatusNotValid(): boolean {
    if (this.checkedSignatoryStatus === SignatoryStatus.NIET_IN_ORDE) {
      return true;
    }
    return false;
  }

  getInitialsErrorKey(): string | undefined {
    const formTagId = "initials";
    let errorMessage;
    if (
      this.isTouchedAndDirty("initials") &&
      this.hasError("initials", "required")
    ) {
      errorMessage = this._translate.instant("INPUT.INITIALS.ERROR_REQUIRED");
    } else if (
      this.isSignatoryStatusNotValid() === true &&
      !this.hasError("initials", "required")
    ) {
      errorMessage = this._translate.instant("INPUT.INITIALS.NO_SIGNATORY");
    } else {
      errorMessage = undefined;
    }
    if (errorMessage && errorMessage !== this.lastReportedInitialsError) {
      this.sendError.emit({
        error: "form_error",
        formTagId: formTagId,
        errorMessage: errorMessage,
      });
      this.lastReportedInitialsError = errorMessage; 
    }

    if (!errorMessage) {
      this.lastReportedInitialsError = undefined;
    }

    return errorMessage;
  }

  getLastNameErrorKey(): string | undefined {
    const formTagId = "lastName";
    let errorMessage;
    if (
      this.isTouchedAndDirty("lastName") &&
      this.hasError("lastName", "required")
    ) {
      errorMessage = this._translate.instant("INPUT.LASTNAME.ERROR_REQUIRED");
    } else if (
      this.isSignatoryStatusNotValid() === true &&
      !this.hasError("lastName", "required")
    ) {
      errorMessage = this._translate.instant("INPUT.LASTNAME.NO_SIGNATORY");
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastReportedLastNameError) {
      this.sendError.emit({
        error: "form_error",
        formTagId: formTagId,
        errorMessage: errorMessage,
      });
      this.lastReportedLastNameError = errorMessage; 
    }

    if (!errorMessage) {
      this.lastReportedLastNameError = undefined;
    }

    return errorMessage;
  }

  getPhoneNumberErrorKey(): string | undefined {
    const formTagId = "phone";
    let errorMessage;
    if (
      this.isTouchedAndDirty("phoneNumber") &&
      this.hasError("phoneNumber", "required")
    ) {
      errorMessage = this._translate.instant("COMPANY_DATA.INPUT.PHONE_NUMBER.ERROR_REQUIRED");
    } else if (
      this.isTouchedAndDirty("phoneNumber") &&
      this.hasError("phoneNumber", "pattern") &&
      !this.hasError("phoneNumber", "required")
    ) {
      errorMessage = this._translate.instant("COMPANY_DATA.INPUT.PHONE_NUMBER.ERROR_INVALID")
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastReportedPhoneNumberNameError) {
      this.sendError.emit({
        error: "form_error",
        formTagId: formTagId,
        errorMessage: errorMessage,
      });
      this.lastReportedPhoneNumberNameError = errorMessage; 
    }

    if (!errorMessage) {
      this.lastReportedPhoneNumberNameError = undefined;
    }

    return errorMessage;
  }

  getEmailErrorKey(): string | undefined {
    const formTagId = "Email";
    let errorMessage;
    if (this.isTouchedAndDirty("email") && this.hasError("email", "required")) {
      errorMessage = this._translate.instant( "INPUT.EMAIL.ERROR_REQUIRED");
    } else if (
      this.isTouchedAndDirty("email") &&
      this.hasError("email", "pattern") &&
      !this.hasError("email", "required")
    ) {
      errorMessage = this._translate.instant("INPUT.EMAIL.ERROR_INVALID");
    } else if (
      this.isTouchedAndDirty("email") &&
      this.hasError("email", "duplicate") &&
      !this.hasError("email", "required")
    ) {
      errorMessage = this._translate.instant( "INPUT.EMAIL.ERROR_DUPLICATE");
    } else {
      errorMessage = undefined;
    }
    if (errorMessage && errorMessage !== this.lastReportedEmailError) {
      this.sendError.emit({
        error: "form_error",
        formTagId: formTagId,
        errorMessage: errorMessage,
      });
      this.lastReportedEmailError = errorMessage; 
    }

    if (!errorMessage) {
      this.lastReportedEmailError = undefined;
    }

    return errorMessage;
  }
}
